//Library Imports
import { useState, useEffect } from "react";
import { Modal, Tabs } from "antd";
import { useDispatch, useSelector } from "react-redux";
import styled from "styled-components";

//Local Imports
import { RootState } from "store";
import SmallButton from "../../../Content/Shared/SmallButton";
import { ReactComponent as Cross } from "../../../../assets/img/icons/cross.svg";
import Arrow from "../../../../assets/img/icons/arrow-left.svg";
import { addActionModal } from "../../actions";
import { ControlNameEnum, ControlName, IListData } from "../../interfaces";
import ListsData from "./ListsData";
import IfElseContent from "./IfElseContent";
import WaitModal from "./WaitModal";
import SendEmail from "./SendEmail";

type Props = {};

const AddAction = (props: Props) => {
  // Redux States
  const actionModal = useSelector((state: RootState) => state.automation.modals.addAction);
  const actionModalVisible: boolean = useSelector((state: RootState) => state.automation.modals.addAction.visible);
  const dropAreaData = useSelector((state: RootState) => state.automation.workflow.dropArea);
  const selectedEmailId: number = useSelector((state: RootState) => state.automation.emails.selectedEmailId);
  const selectedListsIds: number[] = useSelector((state: RootState) => state.automation.lists.selectedListIds);
  const conditionId: number = useSelector((state: RootState) => state.automation.conditionAction.conditionId);
  const conditionEmailId: number = useSelector(
    (state: RootState) => state.automation.conditionAction.conditionEmailId
  );

  // Local States
  const [activeKey, setActiveKey] = useState<any>(ControlNameEnum.SEND_EMAIL);
  const [enableSave, setEnableSave] = useState(false);

  // Local Variables
  const dispatch = useDispatch();

  // Component Logic
  useEffect(() => {
    if (actionModal.visible && dropAreaData.hasOwnProperty("controlName")) {
      if (dropAreaData?.controlName === ControlNameEnum.WAIT) enableSaveButtonState(true); // if delay action is dropped, enable save button
      setActiveKey(dropAreaData?.controlName);
    } else if (actionModal.visible) {
      setActiveKey(ControlNameEnum.SEND_EMAIL);
    }
  }, [dropAreaData]);

  useEffect(() => {
    if (actionModal.id) {
      setActiveKey(actionModal.tab);
      if (actionModal.tab === ControlNameEnum.WAIT) enableSaveButtonState(true);
    }
  }, [actionModal.id]);

  let isTabDisabled = Boolean(actionModal.id);

  const enableSaveButtonState = (value: boolean) => {
    setEnableSave(value);
  };

  const tabPanels = [
    {
      name: "Email",
      key: ControlNameEnum.SEND_EMAIL,
      disabled: isTabDisabled,
      className: "email-tab",
      component: SendEmail,
    },
    {
      name: "Add to a list",
      key: ControlNameEnum.ADD_LIST,
      disabled: isTabDisabled,
      className: "",
      component: ListsData,
    },
    {
      name: "Wait for days/hours",
      key: ControlNameEnum.WAIT,
      disabled: isTabDisabled,
      className: "wait-tab",
      component: WaitModal,
    },
    {
      name: "If / Else",
      key: ControlNameEnum.IF_ELSE,
      disabled: isTabDisabled,
      className: "",
      component: IfElseContent,
    },
  ];

  const handleCancel = () => {
    enableSaveButtonState(false);
    dispatch(addActionModal(false));
  };

  useEffect(() => {
    if (!actionModalVisible) {
      enableSaveButtonState(false);
    }
  }, [actionModalVisible]);

  const tabChangeHandler = (key: any) => {
    setActiveKey(key);
    // in case of delay save button will be enabled by default
    if (key === ControlNameEnum.WAIT) enableSaveButtonState(true);
    else if (key === ControlNameEnum.SEND_EMAIL && selectedEmailId > 0) enableSaveButtonState(true);
    else if (key === ControlNameEnum.ADD_LIST && selectedListsIds.length > 0) enableSaveButtonState(true);
    else if (key === ControlNameEnum.IF_ELSE && conditionId > 0 && conditionEmailId > 0)
      enableSaveButtonState(true);
    else enableSaveButtonState(false);
  };

  return (
    <>
      <StyledModal
        centered
        visible={actionModal.visible}
        // onOk={setIsShow}
        onCancel={handleCancel}
        footer={null}
        closeIcon={<Cross />}
        width={907}
        wrapClassName="workflow-modal"
        title="Add New Action"
        bodyStyle={{ padding: "0px" }}
        maskClosable={false}
        // height={'70vh'}
      >
        <Wrapper>
          <div className="add-action-wrapper">
            <Tabs
              onChange={(key: any) => tabChangeHandler(key)}
              activeKey={`${activeKey}`}
              defaultActiveKey={ControlNameEnum.SEND_EMAIL}
              tabPosition="left"
            >
              {tabPanels.map((tab) => {
                const Component = tab.component;
                return (
                  <Tabs.TabPane
                    tab={tab.name}
                    key={tab.key}
                    disabled={tab.disabled}
                    className={`${tab.className} ${
                      tab.key === ControlNameEnum.ADD_LIST
                        ? "add-to-list"
                        : tab.key === ControlNameEnum.SEND_EMAIL
                        ? "send-email-container"
                        : ""
                      // : tab.key === ControlNameEnum.WAIT ? "":""
                    }`}
                  >
                    <Component id={actionModal.id} enableSaveButton={enableSaveButtonState} />
                  </Tabs.TabPane>
                );
              })}
            </Tabs>
            <div className="d-flex justify-content-end save-btn">
              <SmallButton
                disabled={!enableSave}
                title="Save"
                type="submit"
                form={`${activeKey}`} // On press the Save button, form with same id as in 'activeKey', 'submit' Handler will be called
                // reason of doing is to separate out the logic in their specific component while using the same 'Save' button
              />
            </div>
          </div>
        </Wrapper>
      </StyledModal>
    </>
  );
};

export default AddAction;

const Wrapper = styled.div`
  .add-action-wrapper {
    .ant-tabs {
      overflow: visible;
      .ant-modal-header {
        border-radius: 8px 8px 0 0;
        font-weight: 500;
        font-family: "Roboto", sans-serif;
        font-size: 24px;
        line-height: 38px;
        letter-spacing: -0.666667px;
        color: #5e7079;
      }
      .ant-tabs-nav {
        width: 265px;
        height: 100%;
        margin-bottom: 24px;
        @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
          width: 210px;
        }
        .ant-tabs-ink-bar-animated,
        .ant-tabs-nav-operations {
          display: none;
        }
        .ant-tabs-tab {
          position: relative;
          margin-top: 0;
          padding: 10px 24px;
          .ant-tabs-tab-btn {
            color: #424b50;
            font-size: 16px;
            font-weight: 500;
            font-family: "Roboto", sans-serif;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 12px;
            }
          }
          &.ant-tabs-tab-active,
          &:hover {
            background-color: #e7f6ea;
            .ant-tabs-tab-btn {
              color: #424b50;
              text-shadow: none;
            }
          }
          &:after {
            content: "";
            color: #424b50;
            position: absolute;
            top: 18px;
            right: 18px;
            background-image: url("${Arrow}");
            background-repeat: no-repeat;
            background-size: contain;
            width: 5px;
            height: 11px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              top: 14px;
            }
          }
        }
      }
      .ant-tabs-content-holder {
        border-left: 1px solid transparent;
        margin: 0;
        position: relative;
        &:before {
          content: "";
          width: 1px;
          height: calc(100% + 90px);
          position: absolute;
          top: 0;
          left: 0;
          background-color: #dddddd;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            height: calc(100% + 80px);
          }
        }
        .ant-tabs-tabpane {
          min-height: 478px;
          @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
            min-height: 380px;
            font-size: 16px;
          }
          &.send-email-container {
            padding-left: 0;
          }
          .label-model {
            color: #5e7079;
            font-size: 24px;
            font-weight: 500;
            font-family: "Roboto", sans-serif;
            margin: 18px 0;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              font-size: 16px;
            }
          }
          .overflow-scroll {
            width: 100% !important;
            height: 480px;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              height: 400px;
            }
            .search-wrapper {
              max-width: 250px;
              padding-right: 0;
              margin: 0;
            }
          }
          &.email-tab {
            padding-left: 1px;
          }
          &.wait-tab {
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              /* min-height: 300px; */
              /* height: 382px; */
            }
            > div {
              /* height: 462px; */
            }
          }
          .condition-wrapper {
            width: calc(100% - 50px);
            /* height: 407px; */
            /* height: 100%;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              height: 100%;
              min-height: 300px;
            } */
            .label-condition {
              color: #5e7079;
              font-size: 20px;
              font-weight: 500;
              font-family: "Roboto Slab", serif;
              margin-bottom: 10px;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 14px;
              }
            }
            .add-condition {
              cursor: pointer;
              color: #27ce0a;
              font-size: 14px;
              font-weight: 700;
              font-family: "Roboto", sans-serif;
              margin: 8px 0;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                font-size: 12px;
              }
              img {
                height: 11px;
                vertical-align: baseline;
              }
            }
            label {
              margin: 0 !important;
            }
            div[class$="-control"] {
              min-width: 100%;
              padding-left: 2px;
              height: 44px;
              border-color: #dadada;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                height: 32px;
              }
            }
            input {
              border-color: #dadada;
              padding-right: 15px;
              height: 100%;
            }
            .condition-dropdown {
              border-color: #dadada;
              box-sizing: border-box;
              height: 44px;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                border-radius: 6px;
                height: auto;
              }
              &.ant-select-open {
                cursor: text;
              }
              .ant-select-selector {
                border-radius: 7px;
                height: 44px;
                @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  border-radius: 6px;
                  height: auto;
                }
                .ant-select-selection-item {
                  max-width: 210px;
                  overflow: hidden;
                  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                    max-width: 250px;
                  }
                }
              }
              .ant-select-selection-placeholder {
                text-overflow: ellipsis;
                white-space: nowrap;
                color: #7e7e7e;
                font-size: 16px;
                line-height: 44px;
                @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 12px;
                  line-height: 28px;
                }
              }
              .ant-select-arrow {
                border-left: 1px solid hsl(0, 0%, 80%);
                padding-left: 12px;
                margin-right: 5px;
                .anticon-down {
                  width: 0;
                  height: 0;
                  border-left: 6px solid transparent;
                  border-right: 6px solid transparent;
                  border-top: 8px solid #7e7e7e;
                  svg {
                    opacity: 0;
                  }
                }
                .anticon-search {
                  /* width: 20px;
                  height: 20px; */
                  svg {
                    path {
                      fill: #7e7e7e;
                    }
                  }
                }
              }
              .ant-select-clear {
                top: 20px;
                height: 20px;
                @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  top: 15px;
                }
              }
            }
          }
          .search-wrapper {
            margin-top: 12px;
            margin-bottom: 8px;
            padding-right: 24px;
            width: 100%;
            float: left;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              margin-top: 6px;
              margin-bottom: 4px;
            }
          }
          form {
            position: relative;
            .list-count {
              position: absolute;
              right: 25px;
              top: 70px;
              font-family: "Roboto";
              font-style: normal;
              font-weight: 500;
              font-size: 16px;
              line-height: 18px;
              text-align: right;
              letter-spacing: -0.333333px;
              color: #1ec252;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                top: 60px;
                font-size: 14px;
              }
            }
          }
          .email-list {
            list-style-type: none;
            margin: 0;
            padding: 0;
            width: calc(100% - 10px);
            float: left;
            height: 415px;
            overflow-y: auto;
            scrollbar-width: thin;
            scrollbar-color: #001760 #efefef;
            @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
              height: 320px;
              /* min-height: 300px; */
            }
            &::-webkit-scrollbar {
              width: 5px;
              height: 5px;
              border-radius: 5px;
            }
            /* Track */
            &::-webkit-scrollbar-track {
              background: #fff;
              margin-top: 0;
              margin-bottom: 10px;
              @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                margin-top: 0;
              }
            }
            /* Handle */
            &::-webkit-scrollbar-thumb {
              background: #001760;
            }
            li {
              margin: 8px 0;
              padding: 0;
              max-width: calc(100% - 150px);
              span {
                color: #7e7e7e;
                font-size: 16px;
                font-weight: 500;
                font-family: "Roboto", sans-serif;
                @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 12px;
                }
              }
              .ant-typography {
                color: #7e7e7e;
                font-size: 16px;
                font-weight: 500;
                font-family: "Roboto", sans-serif;
                /* overflow: hidden; */
                /* white-space: nowrap; */
                /* max-width: 50%; */
                /* width: 250px; */
                -webkit-line-clamp: 1;
                /* height: 30px; */
                overflow: hidden;
                white-space: nowrap;
                margin-bottom: 0;
                @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
                  font-size: 12px;
                  padding-top: 2px;
                  line-height: 1;
                }
              }
              .hidden-text {
                height: 1px;
                max-width: 50%;
                overflow: hidden;
                opacity: 0;
                visibility: hidden;
                white-space: nowrap;
                line-height: 1px;
                padding: 0;
                margin: 0;
                float: left;
                margin-top: -1px;
              }
            }
          }
        }
      }
    }
    .save-btn {
      padding: 24px 24px 28px 24px;
    }
    .add-to-list {
      min-height: 300px;
      > div:nth-child(2) {
        float: none;
        width: 100%;
        justify-content: flex-start;
        .inner-wrapper {
          width: 100%;
          .description {
            text-align: center;
          }
        }
      }
    }
  }
`;

const StyledModal = styled(Modal)`
  height: 626px;
  @media all and (max-width: 1439px), (max-height: 729px), (-webkit-device-pixel-ratio: 1.25) {
    height: 500px;
  }

  .ant-modal-content {
    .ant-modal-header {
      border-top-left-radius: 8px;
      border-top-right-radius: 8px;
      .ant-modal-title {
        margin: 0;
        font-family: "Roboto";
        color: #424b50;
        font-weight: 500;
        font-size: 16px;
        line-height: 22px;
        word-wrap: break-word;
      }
    }
    border-radius: 8px;
    box-shadow: 0px 0px 17px -2px rgba(0, 0, 0, 0.0821427);
  }
`;
